// Modals — conversion + content gating. Ported from the WFC redesign.
//
// Built on the native <dialog> + showModal(): the browser gives us the top layer,
// the focus trap and Esc-to-close, so this file only handles opening, hydration,
// the closing animation and the scroll lock.
//
// Markup contract (all delegated off one document listener):
//   [data-wfc-modal="destination|venue|request"]  a trigger
//   [data-venue-name|image|type|location]  venue hydration
//   [data-modal-close]  closes the nearest dialog
//   [data-modal-reset]  request modal: back from the acknowledgment to the form
//
// A trigger whose dialog is not on the page falls through UNHANDLED — the
// lookup returns before preventDefault(). That is load-bearing: the nav and
// footer "Destinations" links carry a gate trigger AND a real href, so on any
// page without the gate dialog they simply navigate and the server decides.

import { pushEvent, formParams } from "./wfc_events.js";

const OPEN_CLASS = "wfc-modal-open";

// Matches the CSS transition on .n-modal__panel (--wfc-dur-mid). Used as the
// fallback timer so a missed transitionend can never strand an open dialog.
const CLOSE_MS = 260;

const dialogFor = (root, name) =>
  root.querySelector(`[data-wfc-dialog="${name}"]`);

const anyOpen = () => !!document.querySelector("dialog.n-modal[open]");

// --- open / close ------------------------------------------------------------

function openDialog(dialog, trigger) {
  if (!dialog || dialog.open) return;
  // Stored PER DIALOG, not in one shared variable: opening the request modal
  // from inside the venue modal closes the venue one asynchronously, and a
  // shared variable would be overwritten before that close finishes — leaving
  // the second dialog with no element to restore focus to.
  dialog.__wfcTrigger = trigger || null;

  dialog.showModal();
  document.documentElement.classList.add(OPEN_CLASS);

  // showModal() focuses the first focusable child — which is the close button,
  // so the dialog opens with a focus ring on "✕". Move focus to the panel
  // instead: the dialog is still announced from the top, and the first Tab
  // lands on the close button as expected.
  const panel = dialog.querySelector(".n-modal__panel");
  if (panel) {
    panel.tabIndex = -1;
    panel.focus();
  }

  // Next frame, so the transition has a start value to animate from.
  // The timer is not belt-and-braces: rAF is throttled to a standstill in a
  // BACKGROUND TAB, so on a dialog opened while the tab is hidden the class
  // would never land — showModal() has already run, so the visitor returns to
  // a scrim, a locked scroll and an invisible panel. setTimeout keeps firing
  // when hidden. classList.add is idempotent, so whichever runs first wins and
  // the other is a no-op. (Same hazard the reveal animations guard against.)
  const reveal = () => {
    dialog.classList.add("is-open");
    syncScrollCue(dialog);
  };
  requestAnimationFrame(reveal);
  setTimeout(reveal, 50);
}

function closeDialog(dialog) {
  if (!dialog || !dialog.open) return;
  dialog.classList.remove("is-open");

  const trigger = dialog.__wfcTrigger;
  dialog.__wfcTrigger = null;

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    dialog.close();

    // Only lift the scroll lock / restore focus if this was the LAST open
    // dialog. Chaining venue → request closes the first while the second is
    // already open; unconditionally clearing here would unlock the page behind
    // a visible modal and steal focus from it.
    if (anyOpen()) return;

    document.documentElement.classList.remove(OPEN_CLASS);
    // Native <dialog> restores focus only on Esc/form close, not on our
    // animated path — do it explicitly, and skip elements that have since been
    // hidden or detached (focusing those would silently drop focus to <body>).
    if (
      trigger &&
      document.contains(trigger) &&
      trigger.offsetParent !== null
    ) {
      trigger.focus();
    }
  };

  const panel = dialog.querySelector(".n-modal__panel");
  if (panel) panel.addEventListener("transitionend", finish, { once: true });
  setTimeout(finish, CLOSE_MS);
}

// --- hydration ---------------------------------------------------------------

// The Venue Quick-View is members-only: photo, name, meta and a "Request
// booking" button that chains into the request modal.
function hydrateDestination(dialog, trigger) {
  const d = trigger.dataset;
  const set = (sel, value) => {
    const el = dialog.querySelector(sel);
    if (el) el.textContent = value || "";
  };

  set("[data-modal-name]", d.destinationName);
  set("[data-modal-country]", d.destinationCountry);

  const image = dialog.querySelector("[data-modal-image]");
  if (image) {
    image.src = d.destinationImage || "";
    image.alt = d.destinationName ? `${d.destinationName} destination` : "";
  }
}

function hydrateVenue(dialog, trigger) {
  const d = trigger.dataset;
  const set = (sel, value) => {
    const el = dialog.querySelector(sel);
    if (el) el.textContent = value || "";
  };

  const meta = [d.venueType, d.venueLocation].filter(Boolean).join(" · ");
  set("[data-venue-detail-meta]", meta);
  set("[data-venue-detail-name]", d.venueName);
  set("[data-venue-detail-description]", d.venueDescription);
  set("[data-venue-detail-location]", d.venueLocation);

  const img = dialog.querySelector("[data-venue-detail-image]");
  if (img) {
    img.src = d.venueImage || "";
    img.alt = d.venueName ? `${d.venueName} interior` : "";
  }

  // The request trigger lives inside this dialog. Carry the selected venue
  // forward before the quick-view is replaced by the request dialog.
  const requestTrigger = dialog.querySelector('[data-wfc-modal="request"]');
  if (requestTrigger) {
    requestTrigger.dataset.venueName = d.venueName || "";
    // request_prefill_click reads data-ga-p-venue (see wfc_events.js).
    requestTrigger.dataset.gaPVenue = d.venueName || "";
  }
}

function hydrateRequest(dialog, trigger) {
  const venue = trigger.dataset.venueName || "";

  const context = dialog.querySelector("[data-booking-venue-name]");
  if (context) context.textContent = venue;

  showPanel(dialog, "form");
}

function showPanel(dialog, name, moveFocus) {
  let shown = null;
  dialog.querySelectorAll("[data-modal-panel]").forEach((p) => {
    const on = p.getAttribute("data-modal-panel") === name;
    p.hidden = !on;
    // FIRST match wins. Each panel now appears TWICE — once as a .n-modal__body
    // and once inside the pinned .n-modal__footer — and the body always comes
    // first in the DOM. That is the copy worth focusing and the one with a
    // heading to label the dialog with.
    if (on && !shown) shown = p;
  });
  if (!shown) return;

  // Retarget the dialog's accessible name whenever the panel changes, NOT only
  // when we also move focus. This used to sit inside the moveFocus guard, so
  // hydrateRequest's plain showPanel(dialog, "form") never ran it: a member who
  // sent one request, closed, and reopened got a dialog that announced itself as
  // "Your concierge has it" over an empty form.
  const heading = shown.querySelector("h2, h3");
  if (heading) {
    if (!heading.id) heading.id = `wfc-modal-panel-${name}`;
    dialog.setAttribute("aria-labelledby", heading.id);
  }

  // Swapping panels hides whatever had focus (the submit button), which drops
  // focus to <body> — the dialog stops taking Esc and a screen reader is told
  // nothing changed. Move focus into the new panel so the change is announced.
  if (moveFocus) {
    shown.tabIndex = -1;
    shown.focus();
  }
  syncScrollCue(dialog);
}

// Is there more to READ? The pinned footer already guarantees the primary action
// is visible, but content above it can still be clipped, and overlay scrollbars
// (the macOS/iOS default) are invisible until touched — so a clipped panel looks
// like a finished one. The fade is applied only when it is actually true.
function syncScrollCue(dialog) {
  dialog.querySelectorAll(".n-modal__body").forEach((body) => {
    const more = body.scrollHeight - body.scrollTop - body.clientHeight > 1;
    body.classList.toggle("has-more", more);
  });
}

// --- wiring ------------------------------------------------------------------

export function initModals(root = document) {
  if (root.__wfcModalsBound) return;
  root.__wfcModalsBound = true;

  root.addEventListener("click", (e) => {
    // Close button
    const closer = e.target.closest("[data-modal-close]");
    if (closer) {
      closeDialog(closer.closest("dialog"));
      return;
    }

    // Back to the form from the acknowledgment
    const reset = e.target.closest("[data-modal-reset]");
    if (reset) {
      const dialog = reset.closest("dialog");
      const form = dialog && dialog.querySelector("[data-modal-form]");
      if (form) form.reset();
      showPanel(dialog, "form", true);
      return;
    }

    // Scrim click. With <dialog> as the flex container, a backdrop click reports
    // the dialog itself as the target — the panel swallows its own clicks.
    if (e.target.matches("dialog.n-modal")) {
      closeDialog(e.target);
      return;
    }

    // Trigger
    const trigger = e.target.closest("[data-wfc-modal]");
    if (!trigger) return;

    const name = trigger.getAttribute("data-wfc-modal");
    const dialog = dialogFor(document, name);
    if (!dialog) return; // e.g. request modal absent for guests

    e.preventDefault();

    if (name === "destination") hydrateDestination(dialog, trigger);
    if (name === "venue") hydrateVenue(dialog, trigger);
    if (name === "request") hydrateRequest(dialog, trigger);

    // Opening the request modal replaces the venue quick-view underneath it.
    // Inherit the PARENT's trigger (the venue card) as the restore target — the
    // in-dialog button that was clicked is about to be hidden, so returning
    // focus to it would drop focus to <body>.
    const openParent = trigger.closest("dialog.n-modal");
    let restoreTo = trigger;
    if (openParent && openParent !== dialog) {
      restoreTo = openParent.__wfcTrigger || trigger;
      closeDialog(openParent);
    }

    openDialog(dialog, restoreTo);
  });

  // Enter / Space on a role="button" trigger (venue cards, the story venue
  // aside) acts like a click, so keyboard users open the modal and the click
  // analytics fire too.
  root.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const trigger =
      e.target.closest && e.target.closest('[data-wfc-modal][role="button"]');
    if (!trigger || trigger !== e.target) return;
    e.preventDefault();
    trigger.click();
  });

  root.addEventListener("submit", (e) => {
    const form = e.target.closest(
      "[data-single-submit]:not([data-modal-form])",
    );
    if (!form) return;
    if (form.dataset.submitting === "true") {
      e.preventDefault();
      return;
    }

    form.dataset.submitting = "true";
    form.querySelectorAll('[type="submit"]').forEach((button) => {
      button.disabled = true;
    });
  });

  // Request submit → acknowledgment panel, in place.
  // Submits over fetch and swaps to the ack panel ONLY on a success response,
  // so the acknowledgment never tells a member "Your concierge has it" for a
  // request the server never saw.
  root.addEventListener("submit", (e) => {
    const form = e.target.closest("[data-modal-form]");
    if (!form) return;
    e.preventDefault();
    if (form.dataset.submitting === "true") return;

    // Belt and braces. A real submit gesture (click, Enter, requestSubmit) is
    // already blocked by native constraint validation before it reaches here,
    // but a programmatic dispatchEvent(new Event("submit")) is NOT — and an
    // empty request is the one thing a human concierge cannot act on. Without
    // this the dialog would answer a blank form with "Your concierge has it".
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    form.dataset.submitting = "true";
    const submitButtons = form.id
      ? root.querySelectorAll(`[type="submit"][form="${form.id}"]`)
      : [];
    submitButtons.forEach((button) => {
      button.disabled = true;
    });

    const dialog = form.closest("dialog");
    const err = dialog && dialog.querySelector("[data-modal-error]");
    if (err) {
      err.hidden = true;
      err.textContent = "";
    }

    // Build the concierge request_details from the open-text field + the venue
    // context carried by the dialog, so the server receives the same payload
    // the inline .n-request-box would send.
    const message = form.querySelector("[data-booking-message]");
    const details = form.querySelector("[data-booking-request-details]");
    const subject = form.querySelector("[data-booking-request-subject]");
    const venueName =
      dialog && dialog.querySelector("[data-booking-venue-name]");
    const venueNameField = form.querySelector(
      "[data-booking-request-venue-name]",
    );
    if (venueNameField && venueName && venueName.textContent) {
      venueNameField.value = venueName.textContent;
    }
    if (subject && venueName && venueName.textContent) {
      subject.value = `${subject.defaultValue} - ${venueName.textContent}`;
    }
    if (details && message) {
      details.value =
        venueName && venueName.textContent
          ? `${venueName.textContent}: ${message.value}`
          : message.value;
    }

    const echo = dialog && dialog.querySelector("[data-modal-ack-echo]");
    const what = message && message.value.trim();

    // GA4: request_submitted only after the server accepts the request. Read
    // the params now: form.reset() clears the fields on success.
    const gaEvent = form.getAttribute("data-ga-success-event");
    const gaParams = gaEvent ? formParams(form) : null;

    fetch(form.action, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": form.querySelector('[name="authenticity_token"]').value,
      },
      body: new FormData(form),
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Request failed");
        if (gaEvent) {
          try {
            pushEvent(gaEvent, gaParams);
          } catch (_) {}
        }
        if (echo) {
          echo.textContent = what
            ? `"${what}" — you will hear back shortly.`
            : "You will hear back shortly.";
        }
        form.reset();
        showPanel(dialog, "ack", true);
      })
      .catch(() => {
        if (err) {
          err.hidden = false;
          err.textContent =
            "Something went wrong sending your request. Please try again.";
        }
      })
      .finally(() => {
        form.dataset.submitting = "false";
        submitButtons.forEach((button) => {
          button.disabled = false;
        });
      });
  });

  // Keep the "more to read" fade honest. Capture phase because `scroll` does not
  // bubble. Class-toggle only — no layout writes — so this is cheap enough to
  // run unthrottled.
  root.addEventListener(
    "scroll",
    (e) => {
      const body = e.target;
      if (!body || !body.classList || !body.classList.contains("n-modal__body"))
        return;
      syncScrollCue(body.closest("dialog"));
    },
    true,
  );

  // Native Esc dispatches `cancel` and otherwise closes the dialog without
  // passing through our animated close path, leaving the page scroll-locked.
  document
    .querySelectorAll("dialog.n-modal:not([data-modal-bound])")
    .forEach((dialog) => {
      dialog.setAttribute("data-modal-bound", "");
      dialog.addEventListener("cancel", (e) => {
        e.preventDefault();
        closeDialog(dialog);
      });
    });
}

// Turbolinks snapshots must never contain a dialog that is open in the top
// layer; the restored snapshot would otherwise show an invisible/unstyled
// modal and retain the scroll lock.
export function closeModals() {
  document.querySelectorAll("dialog.n-modal[open]").forEach((dialog) => {
    dialog.classList.remove("is-open");
    dialog.__wfcTrigger = null;
    dialog.close();
  });
  document.documentElement.classList.remove(OPEN_CLASS);
}
